<template>
  <BasicTemplate>
    <v-card>

      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0" data-testid="feeds-page-title">
          <v-icon class="mr-2">mdi-rss</v-icon>
          Feeds
        </h3>
        <v-spacer />
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mr-2"
              color="warning"
              outlined
              data-testid="feeds-clear-cache-btn"
              v-bind="attrs"
              v-on="on"
              @click="clearAllCache"
            >
              <v-icon left>mdi-cached</v-icon>
              Clear all feeds cache
            </v-btn>
          </template>
          <span>Purge the cache (game lists and sitemaps) for all feeds</span>
        </v-tooltip>
        <v-btn
          class="mr-2"
          color="info"
          :loading="loading"
          data-testid="feeds-refresh-btn"
          @click="load"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          color="success"
          data-testid="feeds-create-btn"
          @click="$router.push('/feeds/new')"
        >
          Create
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          label="Search"
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          hide-details
          placeholder="Feed name, domain, or id"
        />
        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="displayItems"
          item-key="id"
          show-expand
          :expanded.sync="expanded"
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <div class="d-flex align-center">
              <v-btn
                icon
                small
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :data-testid="'feeds-expand-' + item.id"
                @click.stop="expand(!isExpanded)"
              >
                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
              <span class="caption text--secondary text-no-wrap">{{ feedDomainsSummary(item) }}</span>
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="feed-domains-expanded px-6 py-4 grey lighten-5">
                <div class="text-caption text--secondary mb-2">Domains</div>
                <ul v-if="feedDomainList(item).length" class="mb-0">
                  <li v-for="(d, i) in feedDomainList(item)" :key="'d-' + item.id + '-' + i" class="py-1">
                    {{ d.name }}
                  </li>
                </ul>
                <span v-else class="text--secondary font-italic">No domains</span>
              </div>
            </td>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="feeds-list__actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'feeds-edit-' + item.id"
                    @click="editItem(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'feeds-clone-' + item.id"
                    @click="cloneItem(item)"
                  >
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>Clone</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'feeds-clear-cache-' + item.id"
                    @click="clearCache(item)"
                  >
                    mdi-cached
                  </v-icon>
                </template>
                <span>Clear cache</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="secondary"
                    tag="a"
                    :href="changeLogHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :data-testid="'feeds-history-' + item.id"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-history
                  </v-icon>
                </template>
                <span>View change history</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="warning"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'feeds-delete-' + item.id"
                    @click="deleteItem(item)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-dialog v-model="showCloneDialog" width="500" persistent>
        <v-card>
          <v-card-title>Cloning feed...</v-card-title>
          <v-card-text>
            <v-progress-linear :value="cloneProgress" :buffer-value="100"></v-progress-linear>
            <div class="text-center mt-2">{{ cloneProgressText }}</div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';

export default {
  name: 'FeedsList',
  components: { BasicTemplate },
  data() {
    return {
      search: '',
      loading: false,
      items: [],
      showCloneDialog: false,
      cloneProgress: 0,
      cloneProgressText: '',
      expanded: [],
      headers: [
        { text: 'Id', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Environment', value: 'environment' },
        { text: 'Actions', value: 'actions', sortable: false, width: '160px', align: 'center' },
      ],
    };
  },
  computed: {
    displayItems() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) {
        return this.items;
      }
      return this.items.filter((item) => {
        if (String(item.id).toLowerCase().includes(q)) {
          return true;
        }
        if (item.name.toLowerCase().includes(q)) {
          return true;
        }
        return (item.domains || []).some((d) => d.name && d.name.toLowerCase().includes(q));
      });
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    changeLogLink(item) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'feed',
          entityId: String(item.id),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    async load() {
      this.loading = true;
      try {
        this.items = await this.$gameApi.get('/feeds');
        this.expanded = [];
      } catch (error) {
        console.error('Error loading feeds:', error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    feedDomainList(item) {
      return (item.domains || []).filter((d) => d && d.name);
    },
    feedDomainsSummary(item) {
      const n = this.feedDomainList(item).length;
      if (n === 0) {
        return 'No domains';
      }
      return n === 1 ? '1 domain' : `${n} domains`;
    },
    chunkArray(items, chunkSize) {
      const chunks = [];
      for (let i = 0; i < items.length; i += chunkSize) {
        chunks.push(items.slice(i, i + chunkSize));
      }
      return chunks;
    },
    async runFeedGamesBatchInChunks({ operations, newFeedId, batchSize, getBatchLabel, onBatchResult, onProgress }) {
      if (!Array.isArray(operations) || operations.length === 0) {
        return;
      }

      const chunks = this.chunkArray(operations, batchSize);

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunkOperations = chunks[chunkIndex];
        this.cloneProgressText = getBatchLabel ? getBatchLabel(chunkIndex + 1, chunks.length) : '';

        const batchResult = await this.$gameApi.post(`/feeds/${newFeedId}/games/batch`, {
          operations: chunkOperations,
        });

        if (!batchResult?.success) {
          const firstError = batchResult?.results?.find((r) => r?.error)?.error || 'Batch operation failed';
          throw new Error(firstError);
        }

        if (onBatchResult) {
          await onBatchResult(batchResult, chunkOperations, chunkIndex);
        }

        if (onProgress) {
          onProgress(chunkOperations.length);
        }
      }
    },
    buildCreateFeedGameOperations(feedGames, newFeedId) {
      return feedGames.map((sourceFeedGame) => ({
        operation: 'insert',
        data: this.sanitizeFeedGameForClone(sourceFeedGame, newFeedId),
      }));
    },
    async createFeedGamesInBatches(feedGames, newFeedId, batchSize, createdFeedGameIdByGameId, onProgress) {
      const createOperations = this.buildCreateFeedGameOperations(feedGames, newFeedId);
      await this.runFeedGamesBatchInChunks({
        operations: createOperations,
        newFeedId,
        batchSize,
        getBatchLabel: (current, total) => `Creating FeedGames batch ${current}/${total}...`,
        onBatchResult: (batchResult) => {
          for (let i = 0; i < batchResult.results.length; i++) {
            const created = batchResult.results[i]?.result;
            if (created?.gameId && created?.id) {
              createdFeedGameIdByGameId.set(created.gameId, created.id);
            }
          }
        },
        onProgress,
      });
    },
    sanitizeFeedGameForClone(feedGame, newFeedId) {
      if (!feedGame) {
        return null;
      }

      // Keep all FeedGame scalar properties and remove service/relation fields.
      const disallowedKeys = new Set([
        'id',
        'feedId',
        'feed',
        'game',
        'metaTemplate',
        'feedGameMetas',
      ]);

      const newFeedGame = Object.keys(feedGame).reduce((acc, key) => {
        if (!disallowedKeys.has(key) && feedGame[key] !== undefined) {
          acc[key] = feedGame[key];
        }
        return acc;
      }, {});

      newFeedGame.feedId = newFeedId;
      newFeedGame.gameId = feedGame.gameId;

      if (feedGame.feedGamePrerollSettings) {
        const { feedGameId, feedGame, ...prerollSettings } = feedGame.feedGamePrerollSettings;
        newFeedGame.feedGamePrerollSettings = prerollSettings;
      }

      if (feedGame.supportInfo) {
        const { feedGameId, feedGame, ...supportInfo } = feedGame.supportInfo;
        newFeedGame.supportInfo = supportInfo;
      }

      return newFeedGame;
    },
    async editItem(item) {
      this.$router.push(`/feeds/${item.id}`);
    },
    async clearCache(item) {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: `Clear cache for feed "${item.name}"?`,
        text: 'This purges the cached data (games list per locale and sitemaps) for this feed.',
        showCancelButton: true,
        confirmButtonText: 'Clear cache',
      });
      if (!isConfirmed) {
        return;
      }
      const status = await this.$gameApi.delete(`/feeds/cache/${item.id}`);
      if (status >= 200 && status < 300) {
        this.$swal.fire({ icon: 'success', title: 'Cache cleared', text: `Cache cleared for feed: ${item.name}`, timer: 2000, showConfirmButton: false });
      } else {
        this.$swal.fire({ icon: 'error', title: 'Failed to clear cache', text: `Request failed with status ${status}.` });
      }
    },
    async clearAllCache() {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Clear cache for all feeds?',
        text: 'This purges the cached data (games lists and sitemaps) for every feed.',
        showCancelButton: true,
        confirmButtonText: 'Clear cache',
      });
      if (!isConfirmed) {
        return;
      }
      const status = await this.$gameApi.delete('/feeds/cache/all');
      if (status >= 200 && status < 300) {
        this.$swal.fire({ icon: 'success', title: 'Cache cleared', text: 'Cache cleared for all feeds.', timer: 2000, showConfirmButton: false });
      } else {
        this.$swal.fire({ icon: 'error', title: 'Failed to clear cache', text: `Request failed with status ${status}.` });
      }
    },
    async deleteItem(item) {
      const { isConfirmed } = await this.$swal.fire({ icon: 'warning', title: 'This will delete all feed infromation. Are you sure to proceed?', showCancelButton: true });
      if (isConfirmed) {
        await this.$gameApi.delete(`/feeds/${item.id}`);
        this.items = await this.$gameApi.get('/feeds/');
      }
    },
    async cloneItem(item) {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: `Clone feed "${item.name}"?`,
        text: 'This will create a copy of the feed and clone all associated games.',
        showCancelButton: true,
        confirmButtonText: 'Clone',
      });
      if (!isConfirmed) {
        return;
      }

      try {
        this.showCloneDialog = true;
        this.cloneProgress = 0;
        this.cloneProgressText = 'Loading feed data...';
        let completedSteps = 0;

        const feed = await this.$gameApi.get(`/feeds/${item.id}`);

        const feedGames = feed.feedGames || [];
        const totalGames = feedGames.length;
        const batchSize = 50;
        const totalSteps = 2 + totalGames;

        this.cloneProgress = Math.round((++completedSteps / totalSteps) * 100);
        this.cloneProgressText = 'Creating new feed...';

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const newFeed = await this.$gameApi.post('/feeds', {
          name: `${item.name}_Clone_${timestamp}`,
          environment: item.environment,
        });
        this.cloneProgress = Math.round((++completedSteps / totalSteps) * 100);

        const createdFeedGameIdByGameId = new Map();

        const onProgress = (steps) => {
          completedSteps += steps;
          this.cloneProgress = Math.round((completedSteps / totalSteps) * 100);
        };

        await this.createFeedGamesInBatches(feedGames, newFeed.id, batchSize, createdFeedGameIdByGameId, onProgress);

        this.cloneProgress = 100;
        this.cloneProgressText = 'Done!';
        this.showCloneDialog = false;

        await this.$swal.fire({
          icon: 'success',
          title: 'Feed Cloned',
          text: `Successfully cloned feed: ${newFeed.name}`,
          timer: 2000,
          showConfirmButton: false
        });
        this.$router.push(`/feeds/${newFeed.id}`);
      } catch (error) {
        this.showCloneDialog = false;
        this.cloneProgress = 0;
        this.cloneProgressText = '';

        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to clone feed';
        this.$swal.fire({
          icon: 'error',
          title: 'Clone Failed',
          text: errorMessage,
          confirmButtonText: 'OK'
        });

        console.error('Error cloning feed:', error);
      }
    }
  }
};
</script>

<style scoped>
.feeds-list__actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

::v-deep th:last-child,
::v-deep td:last-child {
  width: 160px !important;
  min-width: 160px !important;
  max-width: 160px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
  white-space: nowrap;
}
</style>

